"use client";
import { useRef, useState } from "react";
import styles from "./MediaSlideshow.module.css";
import { Icon } from "@zapier/zinnia-icons";
import { BetaCarousel, VisuallyHidden } from "@zapier/design-system";
import {
  emitUserInteractionEvent,
  UserInteractionEventName,
} from "src/observability/tracking";
import classNames from "classnames";
import { Template } from "src/types";

export type MediaItem = {
  type: "image" | "video";
  src: string;
  alt?: string;
};

function VideoItem({
  src,
  isThumbnail,
}: {
  src: string;
  isThumbnail: boolean;
}) {
  return isThumbnail ? (
    <div className={styles.videoThumbnail}>
      <video
        src={`${src}#t=1`}
        disablePictureInPicture
        disableRemotePlayback
        muted
      />
      <div className={styles.playIcon}>
        <Icon name="actionPlayFill" size={20} color="PrimeWhite" />
      </div>
    </div>
  ) : (
    <video
      src={src}
      controls
      disablePictureInPicture
      disableRemotePlayback
      controlsList="nodownload"
    >
      <track default kind="captions" srcLang="en" src="" />
    </video>
  );
}

export default function MediaSlideshow({
  mediaItems,
  template,
}: {
  mediaItems: MediaItem[];
  template: Template;
}) {
  const [currentItem, setCurrentItem] = useState<number>(0);

  const [isImageSixteenByNine, setIsImageSixteenByNine] =
    useState<boolean>(false);

  const imgRef = useRef<HTMLImageElement>(null);

  const handleImageLoad = () => {
    const img = imgRef.current;
    if (img) {
      const width = img.naturalWidth;
      const height = img.naturalHeight;
      const actualRatio = width / height;
      const expectedRatio = 16 / 9;
      const threshold = 0.01; // tolerance for float comparison

      setIsImageSixteenByNine(
        Math.abs(actualRatio - expectedRatio) < threshold,
      );
    }
  };

  const handleCarouselArrowClick = (direction: "left" | "right") => {
    emitUserInteractionEvent(
      UserInteractionEventName.mediaSlideshowCarouselArrowClicked,
      {
        event_action: "click",
        option_selected: direction,
        custom_tracking_fields: {
          template_id: template.id,
        },
      },
    );
  };

  const handleThumbnailSelected = (index: number) => {
    emitUserInteractionEvent(
      UserInteractionEventName.mediaSlideshowItemSelected,
      {
        event_action: "click",
        option_selected: index.toString(),
        custom_tracking_fields: {
          template_id: template.id,
        },
      },
    );
  };

  return mediaItems && mediaItems.length > 0 ? (
    <div>
      <div
        className={styles.currentItem}
        id="current-media-item"
        role="tabpanel"
        aria-labelledby={`media-item-${currentItem}`}
      >
        {mediaItems[currentItem]?.type === "image" ? (
          <img
            src={mediaItems[currentItem]?.src}
            alt={mediaItems[currentItem]?.alt || ""}
            className={classNames(styles.mainImage, {
              [styles.mainImageWithPadding]:
                currentItem !== 0 && !isImageSixteenByNine,
            })}
            ref={imgRef}
            onLoad={handleImageLoad}
            fetchPriority={currentItem === 0 ? "high" : "auto"}
          />
        ) : (
          <VideoItem
            src={mediaItems[currentItem]?.src || ""}
            isThumbnail={false}
          />
        )}
      </div>
      {mediaItems.length > 1 && (
        <BetaCarousel
          elementWidth={120}
          gap={16}
          paddingBottom={1}
          className={styles.carousel}
          role="tablist"
          aria-label="Media slides"
          onLeftArrowClick={() => handleCarouselArrowClick("left")}
          onRightArrowClick={() => handleCarouselArrowClick("right")}
        >
          {mediaItems.map((item, index) => (
            <button
              role="tab"
              id={`media-item-${index}`}
              aria-controls="current-media-item"
              key={index}
              className={styles.mediaItemThumbnail}
              onFocus={() => {
                setCurrentItem(index);
                handleThumbnailSelected(index);
              }}
              aria-selected={currentItem === index}
            >
              <VisuallyHidden>Thumbnail of {item.type} </VisuallyHidden>
              {item.type === "image" ? (
                <img
                  src={item.src}
                  alt={item.alt || ""}
                  className={styles.thumbnailImage}
                />
              ) : (
                <>
                  <VideoItem src={item.src} isThumbnail={true} />
                  {item.alt && <VisuallyHidden>{item.alt}</VisuallyHidden>}
                </>
              )}
            </button>
          ))}
        </BetaCarousel>
      )}
    </div>
  ) : null;
}
